import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Heart, Search as SearchIcon, BookOpen, ShieldCheck, Sparkles, LogIn, UserPlus } from "lucide-react";
import { FreeForLifeBanner } from "@/components/welp/FreeBanner";
import Nav from "@/components/welp/Nav";
import PageBg, { POSTER_P1_LANDING } from "@/components/welp/PageBg";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

function HeroSearchForm() {
  const nav = useNavigate();
  const [username, setUsername] = useState("");
  const [platform, setPlatform] = useState("");
  const [platforms, setPlatforms] = useState([]);
  useEffect(() => { api.platforms().then(d => setPlatforms(d.platforms)).catch(() => {}); }, []);
  const onSubmit = (e) => {
    e.preventDefault();
    if (!username.trim() || !platform) { nav("/search"); return; }
    nav(`/search?u=${encodeURIComponent(username.trim())}&p=${encodeURIComponent(platform)}`);
  };
  return (
    <form onSubmit={onSubmit} className="flex flex-col md:flex-row gap-3" data-testid="hero-search-form">
      <input type="text" value={username} onChange={e => setUsername(e.target.value)} placeholder="Username or display name"
        className="flex-1 min-w-0 bg-white/70 backdrop-blur-lg border border-black/10 rounded-2xl px-5 py-4 text-[#1A1A1A] text-lg font-semibold outline-none focus:border-[#FF1A82]"
        data-testid="hero-search-username" />
      <select value={platform} onChange={e => setPlatform(e.target.value)}
        className="md:w-56 bg-white/70 backdrop-blur-lg border border-black/10 rounded-2xl px-5 py-4 text-[#1A1A1A] text-lg font-semibold outline-none focus:border-[#FF1A82]"
        data-testid="hero-search-platform">
        <option value="">Platform…</option>
        {platforms.map(p => <option key={p} value={p}>{p}</option>)}
      </select>
      <button type="submit" className="px-7 py-4 rounded-full bg-[#FF1A82] text-white font-bold text-lg inline-flex items-center justify-center gap-2 hover:brightness-110 transition shrink-0" data-testid="hero-search-cta">
        <SearchIcon className="w-5 h-5" /> Search
      </button>
    </form>
  );
}

function StoryParagraph({ children }) {
  return <p className="text-[#1A1A1A] text-lg leading-relaxed">{children}</p>;
}

function VideoHero({ children, className = "" }) {
  return (
    <section className={`relative overflow-hidden ${className}`}>
      <div className="relative z-10">{children}</div>
    </section>
  );
}

function PosterBgSection({ poster, children, className = "" }) {
  return (
    <section className={`relative overflow-hidden ${className}`}>
      {poster && (
        <div className="absolute inset-0 z-0">
          <img src={poster} alt="" className="w-full h-full object-cover" />
        </div>
      )}
      <div className="relative z-10">{children}</div>
    </section>
  );
}

/* ============== LOGGED-OUT LANDING ============== */
function LoggedOutLanding() {
  return (
    <>
      {/* Hero — video background, logo, slogan, and a clear path to sign in/up */}
      <VideoHero className="py-16 md:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center text-veil">
          <div className="text-xs uppercase tracking-[0.32em] text-[#FF1A82] font-bold mb-4">Welcome to Welp</div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-[#1A1A1A] leading-tight">
            Dating with <span className="italic text-[#FF1A82]">integrity.</span>
          </h1>

          <p className="mt-6 text-[#1A1A1A] text-lg leading-relaxed max-w-xl mx-auto font-semibold">
            Welp is a dating tool that helps people make informed relationship decisions through balanced reviews, accountability, respectful communication, and relationship compatibility insights.
          </p>
          <div className="max-w-xl mx-auto text-left">
            <FreeForLifeBanner />
          </div>
          <p className="mt-4 text-[#1A1A1A] text-lg leading-relaxed max-w-xl mx-auto">
            Read the founder's story below, then create your free account to get started.
          </p>

          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center" data-testid="loggedout-top-cta">
            <Link to="/register" className="px-8 py-3.5 rounded-full bg-[#FF1A82] hover:bg-[#6CA300] text-white font-medium text-lg inline-flex items-center justify-center gap-2 shadow-md transition-colors" data-testid="lo-signup">
              <UserPlus className="w-5 h-5" /> Sign Up Free
            </Link>
            <Link to="/login" className="px-8 py-3.5 rounded-full border-2 border-[#FF1A82] text-[#1A1A1A] font-medium text-lg inline-flex items-center justify-center gap-2 hover:bg-[#FF1A82]/5 transition-colors bg-white/70" data-testid="lo-signin">
              <LogIn className="w-5 h-5 text-[#FF1A82]" /> Sign In
            </Link>
          </div>

          <Link to="/purpose" data-testid="lo-purpose-teaser"
            className="mt-8 mx-auto max-w-xl block rounded-2xl bg-white/80 backdrop-blur-lg border-2 border-[#10B981]/50 p-5 text-left hover:bg-[#10B981]/5 transition-colors">
            <div className="flex items-center gap-2 text-lg font-bold text-[#10B981]"><Heart className="w-5 h-5 fill-[#10B981]" /> The Purpose</div>
            <p className="mt-1 text-lg text-[#1A1A1A] font-semibold">"Instead of allowing that heartbreak to harden me, I decided to transmute it…" — read the founder's full story <ArrowRight className="inline w-4 h-4 text-[#10B981]" /></p>
          </Link>
        </div>
      </VideoHero>
      <PosterBgSection className="py-14 md:py-20">
        <div className="max-w-3xl mx-auto px-6 md:px-10 text-veil">
          <div className="text-sm uppercase tracking-[0.32em] text-[#FF1A82] font-bold mb-4">My Story</div>
          <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-[#1A1A1A] leading-[1.0]">
            My Story, <span className="font-logo italic text-[#FF1A82]">Welp.</span>
          </h2>

          <div className="space-y-6 mt-10">
            <StoryParagraph>
              When I first started navigating the dating world, I had no idea who I was, what I truly wanted, or even what kind of partner was right for me. I lacked confidence, but I also didn't want to be alone. As someone naturally shy, dating apps felt like the easiest way to put myself out there without having to fully step into the spotlight.
            </StoryParagraph>

            <h3 className="font-heading text-3xl md:text-4xl font-bold text-[#FF1A82] italic pt-4">What a Rollercoaster ride!</h3>

            <StoryParagraph>
              Over the years, I went on countless dates trying to find someone who was genuinely who they claimed to be. I experienced everything from people lying about their age or appearance, to dates where there was little to no effort, conversations that ended in ghosting, connections that started intensely and disappeared just as fast, and situations where intentions were completely different from what was presented.
            </StoryParagraph>

            <StoryParagraph>
              The hardest experiences weren't always the obviously bad ones. Sometimes the most painful moments came from believing you had finally found someone different, someone real, only to later discover that the connection was built on dishonesty.
            </StoryParagraph>

            <StoryParagraph>That experience forced me to ask myself a question:</StoryParagraph>

            <blockquote className="border-l-4 border-[#FF1A82] pl-6 py-2 text-[#1A1A1A] text-xl md:text-2xl font-bold italic">
              Why can we review businesses, products, and services, but have no meaningful tools to help us navigate one of the most emotionally impactful parts of our lives?
            </blockquote>

            <StoryParagraph>
              Why is it so hard to say I'm sorry to someone you believed had great potential? Especially when all I want is another chance. Mistakes happen. It's what we do. That mistake shouldn't have to last forever.
            </StoryParagraph>

            <StoryParagraph>
              Dating today often feels fast, disposable, and disconnected. There's very little accountability, and people can easily move from one connection to the next without considering the emotional impact they leave behind.
            </StoryParagraph>
          </div>
        </div>
      </PosterBgSection>

      {/* Why I Built This */}
      <PosterBgSection className="py-14 md:py-20">
        <div className="max-w-3xl mx-auto px-6 md:px-10 text-veil">
          <div className="text-sm uppercase tracking-[0.32em] text-[#FF1A82] font-bold mb-4">Why I Built This</div>
          <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-[#1A1A1A] leading-[1.0]">
            From heartbreak <span className="italic text-[#FF1A82]">to healing.</span>
          </h2>
          <div className="space-y-6 mt-10">
            <StoryParagraph>
              My last heartbreak was worse than any I've experienced before. I found myself wishing there had been a way to know more before investing so deeply. Not to judge someone unfairly, but to better understand patterns, experiences, and intentions. I also found myself wishing there was a way to help the next person avoid the same pain.
            </StoryParagraph>
            <StoryParagraph>
              Instead of allowing that heartbreak to harden me, I decided to transmute it into something that encourages clarity, communication, and accountability.
            </StoryParagraph>
            <StoryParagraph>
              And ta da, <span className="font-logo text-2xl text-[#FF1A82]">Welp</span> was born.
            </StoryParagraph>
          </div>
        </div>
      </PosterBgSection>

      {/* What Welp Is For */}
      <PosterBgSection className="py-14 md:py-20">
        <div className="max-w-3xl mx-auto px-6 md:px-10 text-veil">
          <div className="text-sm uppercase tracking-[0.32em] text-[#FF1A82] font-bold mb-4">What Welp Is For</div>
          <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-[#1A1A1A] leading-[1.0]">
            A space for <span className="italic text-[#FF1A82]">awareness.</span>
          </h2>
          <div className="space-y-6 mt-10">
            <StoryParagraph>
              <span className="font-logo text-2xl text-[#FF1A82]">Welp</span> was created to help people navigate dating with greater awareness, balanced perspectives, and honest experiences from real interactions. The goal is not to attack or shame people, but to create a space where users can make more informed choices while encouraging better behavior and healthier connections overall.
            </StoryParagraph>
            <StoryParagraph>
              I truly hope <span className="font-logo text-2xl text-[#FF1A82]">Welp</span> helps people feel less confused and lets you use your voice in a healthy, respectful way.
            </StoryParagraph>
          </div>

          {/* Single, clear next step at the end of the story */}
          <div className="mt-12 rounded-3xl bg-white/70 backdrop-blur-lg border-2 border-[#FF1A82] p-7 text-center shadow-lg">
            <h3 className="font-heading text-3xl font-bold text-[#1A1A1A]">Ready to begin?</h3>
            <p className="text-[#1A1A1A] font-semibold mt-2">Create your free account to search profiles, read relationship experiences, respond to reviews, apologize, and explore <span className="text-[#10B981]">Zodiac</span> compatibility.</p>
            <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/register" className="px-7 py-4 rounded-full bg-[#FF1A82] text-white font-bold text-lg inline-flex items-center justify-center gap-2 hover:brightness-110 transition" data-testid="lo-bottom-signup">
                <UserPlus className="w-5 h-5" /> Sign Up Free
              </Link>
              <Link to="/login" className="px-7 py-4 rounded-full bg-white/70 backdrop-blur-lg border-2 border-[#FF1A82] text-[#1A1A1A] font-bold text-lg inline-flex items-center justify-center gap-2 hover:bg-[#FF1A82]/10 transition" data-testid="lo-bottom-signin">
                <LogIn className="w-5 h-5 text-[#FF1A82]" /> Sign In
              </Link>
            </div>
          </div>
        </div>
      </PosterBgSection>
    </>
  );
}

/* ============== LOGGED-IN LANDING ============== */
function LoggedInLanding() {
  return (
    <>
      <VideoHero className="py-14 md:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 text-center text-veil">
          <div className="text-xs uppercase tracking-[0.32em] text-[#FF1A82] font-bold mb-4">Welcome back</div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-[#1A1A1A] leading-tight">
            Search with <span className="italic text-[#FF1A82]">confidence.</span>
          </h1>

          <p className="mt-8 text-[#1A1A1A] text-lg leading-relaxed max-w-2xl mx-auto">
            Real people. Real experiences. <span className="text-[#FF1A82] font-medium">Better decisions.</span>
          </p>

          <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl mx-auto">
            <Link to="/search" className="rounded-2xl bg-white/70 backdrop-blur-lg border-2 border-[#FF1A82] p-5 text-left hover:bg-[#FF1A82]/10 transition" data-testid="tile-search">
              <SearchIcon className="w-7 h-7 text-[#FF1A82]" />
              <div className="font-heading text-xl font-bold text-[#1A1A1A] mt-2">Search</div>
              <div className="text-sm text-[#1A1A1A] font-semibold mt-1">Look up a name on a dating platform.</div>
            </Link>
            <Link to="/review/new" className="rounded-2xl bg-white/70 backdrop-blur-lg border-2 border-[#FF1A82] p-5 text-left hover:bg-[#FF1A82]/10 transition" data-testid="tile-review">
              <BookOpen className="w-7 h-7 text-[#FF1A82]" />
              <div className="font-heading text-xl font-bold text-[#1A1A1A] mt-2">Review</div>
              <div className="text-sm text-[#1A1A1A] font-semibold mt-1">Share an honest experience.</div>
            </Link>
            <Link to="/zodiac" className="rounded-2xl bg-white/70 backdrop-blur-lg border-2 border-[#10B981] p-5 text-left hover:bg-[#10B981]/10 transition" data-testid="tile-zodiac">
              <Sparkles className="w-7 h-7 text-[#10B981]" />
              <div className="font-heading text-xl font-bold text-[#1A1A1A] mt-2">Zodiac</div>
              <div className="text-sm text-[#1A1A1A] font-semibold mt-1">Compatibility &amp; natal charts.</div>
            </Link>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-6 md:px-10 mt-14" />
      </VideoHero>

      <section className="bg-transparent py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-10 text-center">
          <div className="text-sm uppercase tracking-[0.32em] text-[#FF1A82] font-bold mb-3">How It Works</div>
          <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-[#1A1A1A]">How <span className="font-logo text-[#FF1A82]">Welp</span> Works</h2>
          <div className="grid md:grid-cols-3 gap-6 mt-14">
            <StepCard num="1" color="#FF1A82" icon={SearchIcon} title="Search" body="Look up a name or username on a dating platform to see what others have shared." />
            <StepCard num="2" color="#FF1A82" icon={BookOpen} title="Learn" body="Read real experiences, see review patterns, and check the Skinny summary." />
            <StepCard num="3" color="#FF1A82" icon={ShieldCheck} title="Decide" body="Make informed decisions with confidence — protect your peace and your future." />
          </div>
        </div>
      </section>
    </>
  );
}

function StepCard({ num, color, icon: Icon, title, body }) {
  return (
    <div className="relative bg-white/70 backdrop-blur-lg rounded-3xl border border-black/10 p-7 text-left shadow-sm" data-testid={`step-${title.toLowerCase()}`}>
      <div className="absolute -top-4 left-7 w-9 h-9 rounded-full text-white font-bold text-base inline-flex items-center justify-center" style={{ background: color }}>{num}</div>
      <Icon className="w-8 h-8" style={{ color }} />
      <h3 className="font-heading text-3xl mt-4 font-bold text-[#1A1A1A]">{title}</h3>
      <p className="text-[#1A1A1A] mt-2 text-base font-semibold leading-relaxed">{body}</p>
    </div>
  );
}

export default function Landing() {
  const { user, loading } = useAuth();

  return (
    <PageBg poster={POSTER_P1_LANDING} scrim="bg-white/80">
      <Nav hideAuthButtons={!user} />
      {loading ? (
        <div className="max-w-3xl mx-auto px-6 py-20 text-center text-[#3A3A3A] font-semibold">Loading…</div>
      ) : user ? <LoggedInLanding /> : <LoggedOutLanding />}

      <footer className="bg-white/80 backdrop-blur-lg border-t border-black/5 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex flex-col md:flex-row items-center justify-between gap-6">
          <span className="text-xs uppercase tracking-[0.32em] text-[#666666] font-semibold">Dating With <span className="text-[#FF1A82]">Integrity</span></span>
          <div className="flex flex-col items-center md:items-end gap-3">
            <div className="flex gap-5 text-sm font-medium text-[#1A1A1A] flex-wrap justify-center md:justify-end">
              {user && <Link to="/feedback" className="hover:text-[#FF1A82] transition-colors" data-testid="footer-feedback">Feedback</Link>}
              {user && <Link to="/report" className="hover:text-[#10B981] transition-colors" data-testid="footer-report">Report Abuse</Link>}
              <Link to="/contact" className="hover:text-[#FF1A82] transition-colors" data-testid="footer-contact">Contact</Link>
              <Link to="/terms" className="hover:text-[#FF1A82] transition-colors" data-testid="footer-terms">Terms</Link>
              <Link to="/privacy" className="hover:text-[#FF1A82] transition-colors" data-testid="footer-privacy">Privacy</Link>
            </div>
            <p className="text-[#666666] text-xs max-w-md text-center md:text-right">
              All reviews are user-submitted. Welp does not verify any content. Reviews are published as the personal account of the reviewer.
            </p>
          </div>
        </div>
        <div className="mt-8 text-center text-sm py-3 font-medium text-[#666666] border-t border-black/5">
          No judgment. No discrimination. Just honesty. &nbsp;·&nbsp; welp3x3.com &nbsp;·&nbsp; © {new Date().getFullYear()} Welp
        </div>
      </footer>
    </PageBg>
  );
}
